import { SPGameState } from "../../enum/GameState";
import { Input } from "../../lib/Input";
import { Time } from "../../lib/Time";
import Vector2 from "../../math/Vector2";
import { clamp01, lerp } from "../../util";
import { Random } from "../../util/Random";
import SPBogey from "../SPBogey";
import SPGameManager from "../SPGameManager";
import SPPlayer from "../SPPlayer";
import WormMaster from "./WormMaster";

enum WormState {
    Attacking,
    Retreating,
    Repositioning
}

export default class Worm extends SPBogey {

    isHead = false;
    parent: Worm|undefined;
    child: Worm|undefined;
    initialSegments = 30;
    bezierPath: Vector2[] = [];
    attackTimer = 0;
    attackTime = 10;
    attackDist = 512;
    master: WormMaster|undefined;

    protected _state: WormState = WormState.Retreating;

    Start(): void {
        if (this.isHead) {
            let lastSeg: Worm|undefined;
            for (let i = 1; i < this.initialSegments; i++) {
                const [, segment] = this.engine.NewGameObjectWithScript<Worm>(Worm, {
                    position: this.transform.position.add(new Vector2(i * this.size*2, 0)),
                    initialValues: {
                        xpReward: this.xpReward,
                        fireRate: this.fireRate,
                        size: this.size,
                        hp: this.hp,
                        maxHP: this.maxHP,
                        master: this.master,
                        color: this.color
                    }
                });
                segment.SetChild(lastSeg || this);
                lastSeg = segment;
            }
        }

        this.master?.register(this);
    }

    Damage(amt: number): void {
        this.master?.Damage(amt);
        super.Damage(amt);
    }

    Die(crashedIntoPlayer?: boolean): void {
        super.Die(crashedIntoPlayer);
        // this.master?.unregister(this);

        if (this.parent) {
            this.parent.child = undefined;
        }
        if (this.child) {
            this.child.parent = undefined;
        }
    }

    SetParent(parent: Worm) {
        this.parent = parent;
        parent.child = this;
    }

    SetChild(child: Worm) {
        this.child = child;
        child.parent = this;
    }

    GetFullChain() {
        const out: Worm[] = [];
        let top: Worm = this;

        while (top.parent) {
            top = top.parent;
        }

        out.push(top);

        while (top.child) {
            out.push(top.child);
            top = top.child;
        }

        return out;
    }

    lastPos: Vector2 = Vector2.zero;
    AttackingUpdate() {
        const t = clamp01(1 - this.attackTimer / this.attackTime);
        if (t < 0.75) {
            this.bezierPath[this.bezierPath.length - 1] = SPPlayer.Singleton.transform.position.clone();
        }
        this.transform.position = Vector2.berp(t, ...this.bezierPath);
        this.velocity = this.transform.position.sub(this.lastPos).scale(1/Time.deltaTime);
        this.lastPos = this.transform.position.clone();

        this.transform.rotation = this.velocity.normalized;

        this.attackTimer -= Time.deltaTime;

        if (this.attackTimer <= 0) {
            this.attackTimer = 0;
            this._state = WormState.Retreating;
        }
    }

    RepositioningUpdate() {
        const vec = this.bezierPath[0].sub(this.transform.position);
        const dist = vec.magnitude;

        if (dist <= this.size) {
            this.attackTimer = this.attackTime;
            this._state = WormState.Attacking;
        }

        vec.normalize();
        this.acceleration = vec.scale(this.accelerationForce);

        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(this.velocity.scale(this.friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));

        this.transform.rotation = this.velocity.normalized;
    }

    RetreatingUpdate() {
        if (this.velocity.squareMagnitude === 0) {
            // this.velocity = Vector2.right.scale(256);
            let rt: number;

            do {
                rt = Random.float(-Math.PI, Math.PI);
            } while (Math.abs(rt - this.transform.rotation.angle) < 0.2618);

            this.velocity = Vector2.fromRadians(rt).scale(256);
        }
        
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));
        this.transform.rotation = this.velocity.normalized;
        const tail = this.GetFullChain()[0];

        if (
            Vector2.distance(tail.transform.position, SPPlayer.Singleton.transform.position) > this.attackDist &&
            Vector2.distance(this.transform.position, SPPlayer.Singleton.transform.position) > this.attackDist
        ) {
            // const pos = SPPlayer.Singleton.transform.position.add(Vector2.fromAngleDegrees(Random.float(0, 360)).scale(256));
            const pos = this.transform.position.clone();
            const vecToPlayer = SPPlayer.Singleton.transform.position.sub(pos);
            const mid = pos.add(vecToPlayer.scale(0.5));
            this.bezierPath = [pos, mid.add(vecToPlayer.rotate(-Math.PI/2)), SPPlayer.Singleton.transform.position];
            this.attackTimer = this.attackTime;
            this._state = WormState.Attacking;
        }
    }

    MainUpdate() {
        switch (this._state) {
            case WormState.Attacking:
                this.AttackingUpdate();
                break;
            case WormState.Repositioning:
                this.RepositioningUpdate();
                break;
            case WormState.Retreating:
                this.RetreatingUpdate();
                break;
        
            default:
                break;
        }
    }

    LateUpdate(): void {
        if (!this.parent) {
            this.trail?.LateUpdate();
        }
    }

    Update(): void {
        if (!SPGameManager.Singleton.isInGame && SPGameManager.Singleton.state !== SPGameState.GameOver) return;

        if (this.isHead) {
            this.MainUpdate();
        }

        

        if (!this.child && !this.isHead) {
            this.isHead = true;
        }

        if (Vector2.distance(SPPlayer.Singleton.transform.position, this.transform.position) <= SPPlayer.Singleton.size + this.size) {
            SPPlayer.Singleton.Damage(1, {bypassExp: true, attacker: this});
        }

        if (!this.parent) return;

        const vec = this.transform.position.sub(this.parent.transform.position);
        const dist = vec.magnitude;
        vec.normalize();

        this.parent.transform.rotation = this.transform.position.sub(this.parent.transform.position).normalized;

        if (dist !== this.size + this.parent.size) {
            this.parent.transform.position = this.parent.transform.position.add(vec.scale(dist - (this.size + this.parent.size)));
        }
    }

    DrawGizmos(): void {
        if (!this.isHead) return;
        this.ctx.strokeStyle = 'white';
        this.ctx.lineWidth = 1;
        
        for (const pt of this.bezierPath) {
            this.engine.FillCircle(pt, 2);
        }

        const pts: Vector2[] = [];
        if (this.bezierPath.length > 1) {
            for (let i = 0; i <= 1024; i++) {
                const t = i/1024;
                const p = Vector2.berp(t, ...this.bezierPath);
                pts.push(p);
            }
        }

        if (pts.length) this.engine.Line(...pts);
    }

}